import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RefreshCw, Search, AlertTriangle, PlusCircle, Trash2, Save, HelpCircle, Zap } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const money = (v) => `$${(Number(v) || 0).toFixed(2)}`;

/**
 * MajikBonus Reconciliation Panel
 *
 * The vendor's Office UI has a `FILL` / `WITHDRAW` button that lets a
 * cashier top up a Slots account directly, bypassing our platform. This
 * panel closes the loop: admin enters per-player direct-fill / direct-
 * withdraw entries and we insert normal majik_topup / majik_redeem rows
 * tagged `source: "majik_direct_recon"` so admin reports + ambassador
 * commissions immediately reflect them.
 */
export const MajikReconciliationPanel = () => {
    const [players, setPlayers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [includeVendor, setIncludeVendor] = useState(false);
    const [q, setQ] = useState('');
    const [summary, setSummary] = useState(null);
    const [vendorInput, setVendorInput] = useState({ purchase: '', redeem: '', since: '2026-05-14' });
    const [drafts, setDrafts] = useState([]); // { user_id, name, kind, amount, dated_at, note }
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ limit: '400' });
            if (q.trim()) params.set('q', q.trim());
            if (includeVendor) params.set('include_vendor', 'true');
            const { data } = await axios.get(`${API}/admin/majik/reconcile-players?${params}`);
            setPlayers(data.rows || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to load players');
        }
        setLoading(false);
    }, [q, includeVendor]);

    const loadSummary = useCallback(async () => {
        try {
            const sinceIso = vendorInput.since ? new Date(vendorInput.since + 'T00:00:00Z').toISOString() : undefined;
            const params = new URLSearchParams();
            if (sinceIso) params.set('since', sinceIso);
            const { data } = await axios.get(`${API}/admin/majik/reconcile-summary?${params}`);
            setSummary(data);
            // Hydrate vendor inputs from persisted config on first load
            if (data.has_vendor_totals && !vendorInput.purchase) {
                setVendorInput({
                    purchase: String(data.vendor_total_purchase || ''),
                    redeem: String(data.vendor_total_redeem || ''),
                    since: (data.since || '2026-05-14').slice(0, 10),
                });
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to load summary');
        }
    }, [vendorInput.since, vendorInput.purchase]);

    const saveVendorTotals = async () => {
        const sinceIso = vendorInput.since ? new Date(vendorInput.since + 'T00:00:00Z').toISOString() : undefined;
        try {
            await axios.put(`${API}/admin/majik/recon-config`, {
                vendor_total_purchase: Number(vendorInput.purchase) || 0,
                vendor_total_redeem: Number(vendorInput.redeem) || 0,
                since: sinceIso,
            });
            toast.success('Vendor totals saved · shortfall badge will persist across logins');
            // Notify the sidebar to re-fetch the badge
            try { window.dispatchEvent(new CustomEvent('majik-recon-updated')); } catch { /* silent */ }
            loadSummary();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to save vendor totals');
        }
    };

    useEffect(() => { loadSummary(); }, [loadSummary]);
    useEffect(() => { load(); }, [load]);

    const addDraft = (p, kind) => {
        setDrafts(d => [...d, {
            user_id: p.user_id, name: p.name, kind,
            amount: '', dated_at: new Date().toISOString().slice(0, 10), note: '',
        }]);
    };

    const autoSuggestFromVendor = () => {
        if (!includeVendor) {
            toast.error('Turn on "Live vendor lookup" first, then Refresh.');
            return;
        }
        const candidates = players.filter(p => p.gap != null && p.gap > 1); // ignore rounding noise
        if (candidates.length === 0) {
            toast('No positive vendor-gaps found — everyone is in sync or has losses only.');
            return;
        }
        // De-dupe: skip anyone who already has a draft
        const alreadyDrafted = new Set(drafts.map(d => d.user_id + ':' + d.kind));
        const toAdd = candidates
            .filter(p => !alreadyDrafted.has(p.user_id + ':topup'))
            .map(p => ({
                user_id: p.user_id, name: p.name, kind: 'topup',
                amount: p.gap.toFixed(2),
                dated_at: new Date().toISOString().slice(0, 10),
                note: `Auto: vendor balance $${(p.vendor_balance || 0).toFixed(2)} > our expected $${p.our_expected_balance.toFixed(2)}`,
            }));
        if (toAdd.length === 0) {
            toast('All positive-gap players already have drafts queued.');
            return;
        }
        setDrafts(d => [...d, ...toAdd]);
        toast.success(`Suggested ${toAdd.length} direct-fill entr${toAdd.length === 1 ? 'y' : 'ies'}. Review the amounts, then Save all.`);
    };

    const updateDraft = (i, patch) =>
        setDrafts(d => d.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

    const removeDraft = (i) => setDrafts(d => d.filter((_, idx) => idx !== i));

    const submit = async () => {
        const clean = drafts
            .map(r => ({ ...r, amount: Number(r.amount) }))
            .filter(r => r.amount > 0);
        if (clean.length === 0) {
            toast.error('Add at least one entry with a non-zero amount');
            return;
        }
        setSaving(true);
        try {
            const { data } = await axios.post(`${API}/admin/majik/reconcile`, {
                entries: clean.map(r => ({
                    user_id: r.user_id,
                    kind: r.kind,
                    amount: r.amount,
                    dated_at: r.dated_at ? new Date(r.dated_at + 'T12:00:00Z').toISOString() : undefined,
                    note: r.note || undefined,
                })),
                reason: `Vendor direct-fill backfill (${clean.length} entries)`,
            });
            toast.success(`Inserted ${data.inserted} entries · ${data.skipped} skipped (already booked)`);
            setDrafts([]);
            loadSummary();
            load();
            try { window.dispatchEvent(new CustomEvent('majik-recon-updated')); } catch { /* silent */ }
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Save failed');
        }
        setSaving(false);
    };

    const gap = useMemo(() => {
        if (!summary || !vendorInput.purchase || !vendorInput.redeem) return null;
        const vp = Number(vendorInput.purchase) || 0;
        const vr = Number(vendorInput.redeem) || 0;
        return {
            topupGap: vp - summary.our_topups,
            redeemGap: vr - summary.our_redeems,
            netGap: (vp - vr) - summary.our_net,
        };
    }, [summary, vendorInput]);

    return (
        <div id="majik-reconcile-panel" className="card-dark p-5" style={{ borderLeft: '3px solid #F97316' }} data-testid="majik-reconcile-panel">
            <div className="flex items-start gap-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-[#F97316] shrink-0 mt-0.5" />
                <div>
                    <h2 className="text-lg font-bold text-white">MajikBonus reconciliation</h2>
                    <p className="text-xs text-[#A0AAB2]">
                        Bridge direct-fill activity from the MajikBonus Office UI back into our ledger,
                        so admin reports + ambassador commissions reflect reality.
                    </p>
                </div>
            </div>

            {/* Shift totals gap card */}
            <div className="mt-4 rounded-lg border border-white/10 bg-[#0e0e0e] p-4">
                <div className="flex items-center gap-2 mb-3">
                    <HelpCircle className="w-4 h-4 text-[#F97316]" />
                    <span className="text-[10px] uppercase tracking-widest font-bold text-[#F97316]">Shift totals check</span>
                </div>
                <p className="text-[11px] text-[#A0AAB2] mb-3">
                    Paste the vendor&apos;s <b className="text-white">Total Purchase</b> and <b className="text-white">Total Redeem</b> from the MajikBonus Drawer screen. We&apos;ll compute the gap vs. our ledger.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div>
                        <Label className="text-[10px] uppercase text-[#A0AAB2]">Vendor Total Purchase</Label>
                        <Input value={vendorInput.purchase} onChange={e => setVendorInput({ ...vendorInput, purchase: e.target.value })}
                            placeholder="e.g. 53347.43"
                            className="bg-black border-white/10 text-white mt-1"
                            data-testid="majik-vendor-purchase" />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase text-[#A0AAB2]">Vendor Total Redeem</Label>
                        <Input value={vendorInput.redeem} onChange={e => setVendorInput({ ...vendorInput, redeem: e.target.value })}
                            placeholder="e.g. 32153.13"
                            className="bg-black border-white/10 text-white mt-1"
                            data-testid="majik-vendor-redeem" />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase text-[#A0AAB2]">Since (shift start date)</Label>
                        <Input type="date" value={vendorInput.since} onChange={e => setVendorInput({ ...vendorInput, since: e.target.value })}
                            className="bg-black border-white/10 text-white mt-1"
                            data-testid="majik-since" />
                    </div>
                    <div className="flex items-end">
                        <Button onClick={saveVendorTotals} className="w-full bg-[#F97316] text-black hover:bg-[#ea630a]" data-testid="majik-recalc">
                            <RefreshCw className="w-4 h-4 mr-1" /> Save &amp; recalc
                        </Button>
                    </div>
                </div>

                {summary && (
                    <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                        <StatBox label="Our topups" value={money(summary.our_topups)} />
                        <StatBox label="Our redeems" value={money(summary.our_redeems)} />
                        <StatBox label="Our net" value={money(summary.our_net)} tone="emerald" />
                        <StatBox label="Recon adjustments" value={money((summary.recon_topups_included || 0) - (summary.recon_redeems_included || 0))} tone="orange" />
                    </div>
                )}

                {gap && (
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                        <GapCard label="Missing topups" value={gap.topupGap} desc="Direct-fills to record" />
                        <GapCard label="Missing redeems" value={gap.redeemGap} desc="Direct-withdraws to record" invert />
                        <GapCard label="Net profit gap" value={gap.netGap} desc="Ambassador comms shortfall" />
                    </div>
                )}
            </div>

            {/* Player list */}
            <div className="mt-5 rounded-lg border border-white/10 bg-[#0e0e0e] p-4">
                <div className="flex items-center gap-2 flex-wrap mb-3">
                    <div className="relative flex-1 min-w-[180px]">
                        <Search className="w-4 h-4 text-[#A0AAB2] absolute left-2.5 top-1/2 -translate-y-1/2" />
                        <Input value={q} onChange={e => setQ(e.target.value)}
                            placeholder="Search player…"
                            className="h-9 pl-8 bg-black border-white/10 text-white text-sm"
                            data-testid="majik-player-search" />
                    </div>
                    <label className="flex items-center gap-2 text-xs text-[#A0AAB2] cursor-pointer">
                        <input type="checkbox" checked={includeVendor} onChange={e => setIncludeVendor(e.target.checked)} data-testid="majik-toggle-vendor" />
                        Live vendor lookup <span className="text-[10px] text-[#8891A0]">(slow)</span>
                    </label>
                    <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="majik-refresh-players">
                        <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
                    </Button>
                    <Button size="sm"
                        onClick={autoSuggestFromVendor}
                        disabled={!includeVendor || loading}
                        title={includeVendor ? 'Auto-add a Fill draft for every player whose vendor balance exceeds our records' : 'Turn on Live vendor lookup first'}
                        className="bg-[#F97316] text-black hover:bg-[#ea630a] disabled:opacity-50"
                        data-testid="majik-auto-suggest">
                        <Zap className="w-4 h-4 mr-1" /> Auto-suggest from vendor
                    </Button>
                </div>

                <div className="overflow-x-auto rounded-lg border border-white/10">
                    <table className="w-full text-sm">
                        <thead className="bg-black/50 text-[10px] uppercase tracking-wider text-[#A0AAB2]">
                            <tr>
                                <th className="text-left px-3 py-2">Player</th>
                                <th className="text-left px-3 py-2">Slots acct</th>
                                <th className="text-right px-3 py-2">Our topups</th>
                                <th className="text-right px-3 py-2">Our redeems</th>
                                <th className="text-right px-3 py-2">Expected bal</th>
                                {includeVendor && <th className="text-right px-3 py-2">Vendor bal</th>}
                                {includeVendor && <th className="text-right px-3 py-2">Gap</th>}
                                <th className="text-right px-3 py-2">Backfill</th>
                            </tr>
                        </thead>
                        <tbody>
                            {players.length === 0 ? (
                                <tr><td colSpan={includeVendor ? 8 : 6} className="text-center text-[#A0AAB2] py-6">
                                    {loading ? 'Loading…' : 'No players with a Slots account.'}
                                </td></tr>
                            ) : players.map(p => (
                                <tr key={p.user_id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`majik-player-${p.user_id}`}>
                                    <td className="px-3 py-2 text-white">{p.name || '—'}<div className="text-[10px] text-[#A0AAB2] font-mono">{p.phone}</div></td>
                                    <td className="px-3 py-2 text-[#A0AAB2] font-mono text-xs">{p.majik_account}</td>
                                    <td className="px-3 py-2 text-right text-white">{money(p.our_topups)}</td>
                                    <td className="px-3 py-2 text-right text-[#A0AAB2]">{money(p.our_redeems)}</td>
                                    <td className="px-3 py-2 text-right text-white">{money(p.our_expected_balance)}</td>
                                    {includeVendor && (
                                        <td className="px-3 py-2 text-right text-white">
                                            {p.vendor_balance != null ? money(p.vendor_balance) : (p.vendor_error ? <span className="text-[#FF6B6B]" title={p.vendor_error}>err</span> : '—')}
                                        </td>
                                    )}
                                    {includeVendor && (
                                        <td className={`px-3 py-2 text-right font-semibold ${p.gap > 0 ? 'text-[#F97316]' : p.gap < 0 ? 'text-[#4ADE80]' : 'text-[#A0AAB2]'}`}>
                                            {p.gap != null ? money(p.gap) : '—'}
                                        </td>
                                    )}
                                    <td className="px-3 py-2 text-right whitespace-nowrap">
                                        <Button size="sm" variant="ghost" className="text-[#F97316] hover:bg-[#F97316]/10 px-2 h-7" onClick={() => addDraft(p, 'topup')} data-testid={`majik-add-topup-${p.user_id}`}>
                                            + Fill
                                        </Button>
                                        <Button size="sm" variant="ghost" className="text-[#4ADE80] hover:bg-[#4ADE80]/10 px-2 h-7 ml-1" onClick={() => addDraft(p, 'redeem')} data-testid={`majik-add-redeem-${p.user_id}`}>
                                            + Withdraw
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Drafts */}
            {drafts.length > 0 && (
                <div className="mt-5 rounded-lg border border-[#F97316]/30 bg-[#F97316]/[0.05] p-4" data-testid="majik-drafts">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <PlusCircle className="w-4 h-4 text-[#F97316]" />
                            <span className="text-sm font-bold text-white">Pending backfill entries ({drafts.length})</span>
                        </div>
                        <Button onClick={submit} disabled={saving} className="bg-[#F97316] text-black hover:bg-[#ea630a]" data-testid="majik-submit">
                            <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : 'Save all'}
                        </Button>
                    </div>
                    <div className="space-y-2">
                        {drafts.map((r, i) => (
                            <div key={i} className="grid grid-cols-12 gap-2 items-center bg-black/30 rounded-md p-2">
                                <div className="col-span-3 text-sm text-white truncate">
                                    <span className={`inline-block text-[10px] uppercase font-bold px-1.5 py-0.5 rounded mr-2 ${r.kind === 'topup' ? 'bg-[#F97316]/20 text-[#F97316]' : 'bg-[#4ADE80]/20 text-[#4ADE80]'}`}>
                                        {r.kind === 'topup' ? 'FILL' : 'WITHDRAW'}
                                    </span>
                                    {r.name}
                                </div>
                                <Input type="number" placeholder="Amount"
                                    value={r.amount} onChange={e => updateDraft(i, { amount: e.target.value })}
                                    className="col-span-2 bg-black border-white/10 text-white h-9"
                                    data-testid={`draft-amount-${i}`} />
                                <Input type="date" value={r.dated_at} onChange={e => updateDraft(i, { dated_at: e.target.value })}
                                    className="col-span-2 bg-black border-white/10 text-white h-9"
                                    data-testid={`draft-date-${i}`} />
                                <Input placeholder="Note (optional)" value={r.note} onChange={e => updateDraft(i, { note: e.target.value })}
                                    className="col-span-4 bg-black border-white/10 text-white h-9"
                                    data-testid={`draft-note-${i}`} />
                                <button onClick={() => removeDraft(i)} className="col-span-1 text-[#FF6B6B] hover:text-[#ff8888] p-1" data-testid={`draft-remove-${i}`}>
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        ))}
                    </div>
                    <p className="text-[10px] text-[#A0AAB2] mt-3 italic">
                        Each entry inserts a normal <code>majik_topup</code> or <code>majik_redeem</code> transaction tagged with source <code>majik_direct_recon</code>.
                        Duplicate protection is on (user + kind + amount + date). Ambassador commissions will pick these up on the next daily run.
                    </p>
                </div>
            )}
        </div>
    );
};

const StatBox = ({ label, value, tone }) => (
    <div className="rounded-lg bg-black border border-white/10 p-3">
        <div className="text-[10px] uppercase tracking-wider text-[#A0AAB2] font-bold">{label}</div>
        <div className={`text-lg font-bold mt-0.5 ${tone === 'emerald' ? 'text-[#4ADE80]' : tone === 'orange' ? 'text-[#F97316]' : 'text-white'}`}>{value}</div>
    </div>
);

const GapCard = ({ label, value, desc, invert }) => {
    const positive = invert ? value < 0 : value > 0;
    return (
        <div className={`rounded-lg border p-3 ${positive ? 'border-[#F97316]/60 bg-[#F97316]/[0.05]' : 'border-white/10 bg-black'}`}>
            <div className="text-[10px] uppercase tracking-wider text-[#A0AAB2] font-bold">{label}</div>
            <div className={`text-2xl font-bold mt-0.5 ${positive ? 'text-[#F97316]' : 'text-white'}`}>{money(value)}</div>
            <div className="text-[10px] text-[#A0AAB2] mt-1">{desc}</div>
        </div>
    );
};

export default MajikReconciliationPanel;
